import * as React from 'react';
import { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { RentalPlanSelectorKnob } from './Knob.js';
import { Selector, Container } from './styles.js';
import { RentalPlanSelectorTrack } from './Track.js';
import { KeyCodes } from './types.js';
import { calculateKnobPosition, getCurrentPointIndex } from './utils.js';

const RentalPlanSelector = ({ points , pointAccentColors , defaultPointIndex , pointIndex , onPointSelected , isHoverable =false , isDraggable =false , isDisabled =false , hasPlusLabel =false , forceLockedKnob =false , className  })=>{
    const startingPoint = typeof pointIndex === "number" ? pointIndex : defaultPointIndex;
    const containerRef = useRef(null);
    const isShortVersion = points.length <= 2;
    const allPointsHaveAccentColor = points.reduce((result, point)=>{
        return result && (point === null || point === void 0 ? void 0 : point.hasAccentColors) === true;
    }, true);
    const [isInitialized, setIsInitialized] = useState(false);
    const [currentPointIndex, setCurrentPointIndex] = useState(startingPoint);
    const [currentPoint, setCurrentPoint] = useState(points[startingPoint]);
    const [knobPosition, setKnobPosition] = useState({
        x: 0,
        y: 0
    });
    const [isLocked, setIsLocked] = useState(forceLockedKnob || false);
    const [dragInitialLockState, setDragInitialLockState] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const strongifiedPoints = useMemo(()=>JSON.stringify(points), [
        points
    ]);
    const resetKnobPosition = useCallback(()=>{
        if (containerRef.current !== null) {
            const containerWidth = containerRef.current.offsetWidth;
            const newKnobPosition = calculateKnobPosition({
                pointIndex: currentPointIndex,
                containerWidth,
                points
            });
            setKnobPosition(newKnobPosition);
        }
    }, [
        currentPointIndex,
        points
    ]);
    useEffect(()=>{
        resetKnobPosition();
        if (!isInitialized) {
            setIsInitialized(true);
        }
    }, [
        currentPointIndex,
        points,
        isInitialized,
        forceLockedKnob,
        resetKnobPosition
    ]);
    useEffect(()=>{
        setCurrentPointIndex(startingPoint);
    }, [
        startingPoint
    ]);
    useEffect(()=>{
        setCurrentPoint(points[startingPoint || 0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        startingPoint,
        strongifiedPoints,
        startingPoint
    ]);
    const selectNewPoint = useCallback((point)=>{
        if (point.value !== currentPoint.value && !isDisabled) {
            setCurrentPoint(point);
            onPointSelected(point);
            setCurrentPointIndex(points.indexOf(point));
        }
    }, [
        currentPoint.value,
        isDisabled,
        onPointSelected,
        points
    ]);
    const handlePointClick = (point)=>{
        if (!isDisabled) {
            selectNewPoint(point);
        }
    };
    const handlePointHover = (point)=>{
        if (isHoverable && !isLocked && !isDisabled) {
            selectNewPoint(point);
        }
    };
    /* istanbul ignore next */ const handleDragStart = ()=>{
        if (forceLockedKnob || isDisabled) return;
        setDragInitialLockState(isLocked);
        setIsLocked(!isLocked);
    };
    /* istanbul ignore next */ const handleDragStop = ()=>{
        if (isDragging) {
            setIsDragging(false);
            setIsLocked(dragInitialLockState);
        }
    };
    /* istanbul ignore next */ const dragHandler = useCallback((event)=>{
        var _containerRef_current_getBoundingClientRect, _containerRef_current, _containerRef_current1;
        setIsLocked(false);
        setIsDragging(true);
        const containerLeftEdge = (_containerRef_current_getBoundingClientRect = containerRef === null || containerRef === void 0 ? void 0 : (_containerRef_current = containerRef.current) === null || _containerRef_current === void 0 ? void 0 : _containerRef_current.getBoundingClientRect()) === null || _containerRef_current_getBoundingClientRect === void 0 ? void 0 : _containerRef_current_getBoundingClientRect.left;
        const containerWidth = containerRef === null || containerRef === void 0 ? void 0 : (_containerRef_current1 = containerRef.current) === null || _containerRef_current1 === void 0 ? void 0 : _containerRef_current1.offsetWidth;
        if (containerLeftEdge && containerWidth) {
            const oldPointIndex = currentPointIndex;
            const newPointIndex = getCurrentPointIndex({
                currentX: "pageX" in event ? event.pageX : event.touches[0].pageX,
                containerLeftEdge,
                containerWidth,
                points
            });
            if (oldPointIndex !== newPointIndex) {
                setCurrentPointIndex(newPointIndex);
                setCurrentPoint(points[newPointIndex]);
                onPointSelected(points[newPointIndex]);
            }
        }
    }, [
        currentPointIndex,
        onPointSelected,
        points
    ]);
    /* istanbul ignore next */ const handlePointKeyDown = useCallback((e, index)=>{
        if ((e.keyCode === KeyCodes.UP || e.keyCode === KeyCodes.LEFT) && points[index - 1]) {
            // select previous if it exists
            selectNewPoint(points[index - 1]);
            e.stopPropagation();
            e.preventDefault();
        }
        if ((e.keyCode === KeyCodes.DOWN || e.keyCode === KeyCodes.RIGHT) && points[index + 1]) {
            // select next if it exists
            selectNewPoint(points[index + 1]);
            e.stopPropagation();
            e.preventDefault();
        }
    }, [
        selectNewPoint,
        points
    ]);
    useEffect(()=>{
        window.addEventListener("resize", resetKnobPosition, true);
        return function cleanup() {
            window.removeEventListener("resize", resetKnobPosition, true);
        };
    }, [
        resetKnobPosition
    ]);
    return /*#__PURE__*/ React.createElement(Selector, {
        className: className,
        "data-testid": "rental-plan-selector"
    }, /*#__PURE__*/ React.createElement(Container, {
        ref: containerRef
    }, /*#__PURE__*/ React.createElement(RentalPlanSelectorTrack, {
        isShortVersion: isShortVersion,
        points: points,
        handlePointClick: handlePointClick,
        handlePointHover: handlePointHover,
        handlePointKeyDown: handlePointKeyDown,
        pointAccentColors: pointAccentColors,
        allPointsHaveAccentColor: allPointsHaveAccentColor,
        isDisabled: isDisabled,
        hasPlusLabel: hasPlusLabel,
        currentPointIndex: currentPointIndex
    }), isInitialized && /*#__PURE__*/ React.createElement(RentalPlanSelectorKnob, {
        currentPoint: currentPoint,
        knobPosition: knobPosition,
        pointAccentColors: pointAccentColors,
        handleDragStart: handleDragStart,
        handleDragStop: handleDragStop,
        dragHandler: dragHandler,
        isDraggable: isDraggable,
        isDragging: isDragging,
        isLocked: isLocked,
        isDisabled: isDisabled,
        hasPlusLabel: hasPlusLabel
    })));
};

export { RentalPlanSelector };
